import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import QRCode from 'qrcode';
import { AppSvgIconComponent, SignalButton, State } from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { PageHeaderComponent } from '@core/components/page-header/page-header.component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type {
  FormPublishSettingsResponse,
  GetFormApiResponse,
  GetFormDataResponse,
} from '@core/api/workspace/models';
import { GetFormDataResponseStatus } from '@core/api/workspace/models';
import {
  buildEmbedIframe,
  buildEmbedScript,
  buildEmbedWebComponent,
  buildPublishedFormUrl,
  buildShortFormUrl,
  draftRetentionLabel as formatDraftRetentionLabel,
} from '../../utils/form-publish-config';

type EmbedTab = 'iframe' | 'script' | 'webcomponent';
type CopyKind = 'link' | 'embed' | null;
type LinkType = 'standard' | 'short';

@Component({
  selector: 'app-form-published-page',
  standalone: true,
  imports: [PageHeaderComponent, SignalButton, AppSvgIconComponent],
  templateUrl: './form-published-page.html',
  styleUrl: './form-published-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormPublishedPage
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private readonly workspaceApi = inject(WorkspaceAPIService);
  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  readonly loadState = new State<GetFormApiResponse>();
  readonly form = signal<GetFormDataResponse | null>(null);

  readonly useShortLink = signal(false);
  readonly embedTab = signal<EmbedTab>('iframe');
  readonly shareCopied = signal<CopyKind>(null);
  readonly qrDataUrl = signal('');

  readonly publishSettings = computed(
    (): FormPublishSettingsResponse | null => {
      const settings = this.form()?.publishSettings;
      return settings ?? null;
    },
  );

  readonly shortFormUrl = computed(() => {
    const data = this.form();
    const code = data?.shortCode?.trim();
    return code ? buildShortFormUrl(code) : '';
  });

  readonly hasShortLink = computed(() => Boolean(this.shortFormUrl()));

  readonly publishedFormUrl = computed(() => {
    const data = this.form();
    return data
      ? buildPublishedFormUrl(data.workspaceSlug, data.slug)
      : '';
  });

  readonly activeShareUrl = computed(() => {
    if (this.useShortLink() && this.shortFormUrl()) {
      return this.shortFormUrl();
    }
    return this.publishedFormUrl();
  });

  readonly embedSnippet = computed(() => {
    const data = this.form();
    if (!data) {
      return '';
    }
    switch (this.embedTab()) {
      case 'script':
        return buildEmbedScript(data.workspaceSlug, data.slug);
      case 'webcomponent':
        return buildEmbedWebComponent(data.workspaceSlug, data.slug);
      case 'iframe':
      default:
        return buildEmbedIframe(data.workspaceSlug, data.slug);
    }
  });

  readonly isPublished = computed(
    () => this.form()?.status === GetFormDataResponseStatus.published,
  );

  constructor() {
    super();
    effect(() => {
      const url = this.activeShareUrl();
      if (!url) {
        this.qrDataUrl.set('');
        return;
      }
      void this.refreshQr(url);
    });
  }

  ngOnInit(): void {
    const slug = this.route.snapshot.paramMap.get('slug')?.trim() ?? '';
    if (!slug) {
      this.form.set(null);
      return;
    }
    this.loadForm(slug);
  }

  ngOnDestroy(): void {
    if (this.copyTimer != null) {
      clearTimeout(this.copyTimer);
      this.copyTimer = null;
    }
  }

  setLinkType(type: LinkType): void {
    this.useShortLink.set(type === 'short' && this.hasShortLink());
  }

  setEmbedTab(tab: EmbedTab): void {
    this.embedTab.set(tab);
  }

  async copyShare(kind: 'link' | 'embed'): Promise<void> {
    const text = kind === 'link' ? this.activeShareUrl() : this.embedSnippet();
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      this.shareCopied.set(kind);
      if (this.copyTimer != null) {
        clearTimeout(this.copyTimer);
      }
      this.copyTimer = setTimeout(() => {
        this.shareCopied.set(null);
        this.copyTimer = null;
      }, 1800);
    } catch {
      // Clipboard may be unavailable; leave UI unchanged.
    }
  }

  downloadQr(): void {
    const dataUrl = this.qrDataUrl();
    if (!dataUrl) {
      return;
    }
    const slug = this.form()?.slug || 'form';
    const anchor = document.createElement('a');
    anchor.href = dataUrl;
    anchor.download = `${slug}-qr.png`;
    anchor.click();
  }

  availableFromDate(settings: FormPublishSettingsResponse): unknown {
    if (settings.publishTiming === 'scheduled') {
      return settings.scheduledAt;
    }
    return this.form()?.updatedAt;
  }

  formatDateTime(value: unknown): string {
    if (value == null || value === '') {
      return '—';
    }
    const raw = typeof value === 'string' ? value : String(value);
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return raw;
    }
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  visibilityLabel(visibility: string | undefined): string {
    if (!visibility) {
      return '—';
    }
    if (visibility === 'workspace') {
      return 'Specific People';
    }
    if (visibility === 'public') {
      return 'Anyone with the link';
    }
    if (visibility === 'password_protected') {
      return 'Password protected';
    }
    return visibility
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  visibilityHint(visibility: string | undefined): string {
    switch (visibility) {
      case 'workspace':
        return 'Accessible only for invited people';
      case 'password_protected':
        return 'Requires a password to open';
      case 'public':
        return 'Anyone with the link can open';
      default:
        return '';
    }
  }

  maxSubmissionsLabel(value: unknown): string {
    if (value == null || value === '') {
      return 'Unlimited';
    }
    const numeric = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(numeric)) {
      return numeric.toLocaleString();
    }
    return String(value);
  }

  draftRetentionLabel(value: unknown): string {
    if (typeof value === 'number') {
      return formatDraftRetentionLabel(value);
    }
    if (value == null || value === '') {
      return formatDraftRetentionLabel(undefined);
    }
    const numeric = Number(value);
    return formatDraftRetentionLabel(
      Number.isFinite(numeric) ? numeric : undefined,
    );
  }

  submissionCountLabel(): string {
    // Submission totals are not on form details yet; show 0 until wired.
    return '0 submissions';
  }

  approvalHint(required: boolean | undefined): string {
    return required
      ? 'Submissions need approval before they are saved'
      : 'Submissions are saved directly';
  }

  editForm(): void {
    const slug = this.form()?.slug;
    if (slug) {
      void this.router.navigate(['/forms/builder', slug], {
        queryParams: { step: 'form' },
      });
      return;
    }
    void this.router.navigate(['/forms']);
  }

  viewForm(): void {
    const url = this.publishedFormUrl();
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  editSettings(): void {
    const slug = this.form()?.slug;
    if (slug) {
      void this.router.navigate(['/forms/builder', slug], {
        queryParams: { step: 'config' },
      });
      return;
    }
    void this.router.navigate(['/forms']);
  }

  private loadForm(slug: string): void {
    this.executeRequest({
      state: this.loadState,
      request: this.workspaceApi.getFormDetails({ slug }),
      handleError: true,
      onSuccess: (response) => {
        this.form.set(response.data);
      },
      onFailed: () => {
        this.form.set(null);
      },
    });
  }

  private async refreshQr(url: string): Promise<void> {
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        width: 220,
        margin: 2,
        errorCorrectionLevel: 'M',
      });
      this.qrDataUrl.set(dataUrl);
    } catch {
      this.qrDataUrl.set('');
    }
  }
}
